/*
 * Pythal packaging
 *
 * Packaging (Premium Packaging, …) is a normal product that only ships with another product.
 * - A product page offers the packaging that suits it; the choice is added together with the product.
 *   Choosing a different packaging later swaps the one in the bag, since an order has one.
 * - Packaging can also be added first, from its own page; a banner then asks for the product.
 * - Checkout stays locked while the bag has packaging but no product, or more than one packaging.
 * - Some packaging turns free once the bag's products reach data.freeThreshold; the picker shows which
 *   price applies. The discount itself is set up in Shopify admin.
 *
 * Data comes from snippets/pythal-packaging-data.liquid.
 */
(() => {
  if (window.PythalPackaging) return;

  const dataElement = document.getElementById('PythalPackagingData');
  if (!dataElement) return;

  let data;
  try {
    data = JSON.parse(dataElement.textContent);
  } catch (error) {
    console.error('Pythal packaging data is not valid JSON', error);
    return;
  }

  const PACKED_WITH = 'Packed with';

  // Translated copy comes from #PythalPackagingStrings (cart drawer / cart page); English is the fallback.
  function t(key, fallback, values) {
    const element = document.getElementById('PythalPackagingStrings');
    let text = (element && element.dataset[key]) || fallback;
    Object.keys(values || {}).forEach((name) => {
      text = text.split(`{{ ${name} }}`).join(values[name]);
    });
    return text;
  }
  const CHECKOUT_SELECTOR = '[name="checkout"], #checkout, #CartDrawer-Checkout';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const BANNER_DISMISS_KEY = 'pythal-packaging-banner-dismissed';

  const packagingIds = new Set((data.packagingIds || []).map(Number));
  const giftWrapVariantId = Number(data.giftWrapVariantId) || 0;
  const freeThreshold = Number(data.freeThreshold) || 0;
  const cartUrl = (data.cartUrl || '/cart').replace(/\/$/, '');
  const nativeFetch = window.fetch.bind(window);

  let cart = data.cart || { items: [] };
  let pendingPackagingKey = null;
  let pendingSince = 0;

  const isPackaging = (item) =>
    packagingIds.has(Number(item.product_id)) || String(item.product_type || '').toLowerCase() === 'packaging';

  function summarize() {
    const items = cart.items || [];
    const lines = items.filter(isPackaging);
    const packagingCount = lines.reduce((total, item) => total + item.quantity, 0);
    const products = items.filter((item) => !isPackaging(item) && Number(item.variant_id) !== giftWrapVariantId);
    const productCount = products.reduce((total, item) => total + item.quantity, 0);
    const productsTotal = products.reduce((total, item) => total + Number(item.final_line_price || 0), 0);

    let lock = null;
    if (packagingCount > 0 && productCount === 0) lock = 'alone';
    else if (packagingCount > 1) lock = 'many';

    return {
      lines,
      packagingCount,
      productCount,
      lock,
      freeUnlocked: freeThreshold > 0 && productsTotal >= freeThreshold,
      title: lines[0] ? lines[0].product_title : ''
    };
  }

  function postJSON(url, body) {
    return nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(async (response) => {
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json.status) {
        throw new Error(json.description || json.message || `Cart request failed with ${response.status}`);
      }
      return json;
    });
  }

  function refresh() {
    return nativeFetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((json) => {
        cart = json;
        apply();
        return cart;
      });
  }

  let refreshTimer;
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => refresh().catch(() => {}), 120);
  }

  // Every cart change in the theme goes through fetch, so watching it keeps the lock in step.
  window.fetch = function (input, init) {
    const request = nativeFetch(input, init);
    const url = typeof input === 'string' ? input : input && input.url;
    if (url && CART_MUTATION.test(String(url).split('#')[0])) {
      request.then(scheduleRefresh, () => {});
    }
    return request;
  };

  function rerenderCarts() {
    document.querySelectorAll('cart-items, cart-drawer-items').forEach((cartItems) => {
      if (typeof cartItems.onCartUpdate === 'function') cartItems.onCartUpdate();
    });
  }

  /* ---------- Checkout lock ---------- */

  function applyCheckoutButtons(state) {
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      if (state.lock) {
        if (!button.disabled) {
          button.disabled = true;
          button.dataset.pkLocked = 'true';
        }
        button.setAttribute('title', lockMessage(state));
      } else if (button.dataset.pkLocked) {
        button.disabled = false;
        delete button.dataset.pkLocked;
        button.removeAttribute('title');
      }
    });
  }

  function lockMessage(state) {
    if (state.lock === 'alone') return t('lockAlone', 'Add a product to check out. Packaging is sent with a product.');
    if (state.lock === 'many') return t('lockMany', 'Keep one packaging to check out.');
    return '';
  }

  function nudge() {
    const state = summarize();
    const notice = Array.from(document.querySelectorAll('[data-pythal-packaging-lock]')).find(
      (element) => element.offsetParent !== null
    );
    if (notice) {
      notice.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      notice.classList.remove('pk-notice--nudge');
      void notice.offsetWidth;
      notice.classList.add('pk-notice--nudge');
      return;
    }
    if (state.lock === 'alone') showBanner(state, true);
    else window.location.href = cartUrl;
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!summarize().lock) return;
      const target = event.target.closest(`${CHECKOUT_SELECTOR}, a[href*="/checkout"]`);
      if (!target) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      nudge();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (!summarize().lock) return;
      const submitter = event.submitter;
      if (submitter && submitter.getAttribute('name') === 'checkout') {
        event.preventDefault();
        event.stopImmediatePropagation();
        nudge();
      }
    },
    true
  );

  /* ---------- Cart notice actions ---------- */

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-pythal-packaging-action]');
    if (!button || button.disabled) return;
    event.preventDefault();
    button.disabled = true;
    button.classList.add('is-busy');

    let action;
    if (button.dataset.pythalPackagingAction === 'keep-one') {
      const updates = {};
      summarize().lines.forEach((line, index) => {
        updates[line.key] = index === 0 ? 1 : 0;
      });
      action = postJSON(`${cartUrl}/update.js`, { updates });
    } else if (button.dataset.pythalPackagingAction === 'swap') {
      const updates = {};
      summarize().lines.forEach((line) => {
        updates[line.key] = 0;
      });
      action = postJSON(`${cartUrl}/update.js`, { updates }).then(() =>
        postJSON(`${cartUrl}/add.js`, {
          items: [
            {
              id: Number(button.dataset.variantId),
              quantity: 1,
              properties: button.dataset.for ? { [PACKED_WITH]: button.dataset.for } : {}
            }
          ]
        })
      );
    } else {
      action = postJSON(`${cartUrl}/add.js`, {
        items: [
          {
            id: Number(button.dataset.variantId),
            quantity: 1,
            properties: button.dataset.for ? { [PACKED_WITH]: button.dataset.for } : {}
          }
        ]
      });
    }

    action
      .then(() => refresh())
      .then(() => rerenderCarts())
      .catch((error) => {
        button.disabled = false;
        button.classList.remove('is-busy');
        console.error(error);
      });
  });

  /* ---------- "Now choose a product" banner ---------- */

  let banner;
  function buildBanner() {
    banner = document.createElement('div');
    banner.className = 'pk-banner';
    banner.setAttribute('role', 'status');
    banner.hidden = true;
    banner.innerHTML = `
      <span class="pk-banner__mark" aria-hidden="true">
        <svg viewBox="0 0 48 32"><path class="pk-bow__loop" d="M24 16 C14 2 2 6 6 14 C9 21 18 19 24 16 Z"/><path class="pk-bow__loop" d="M24 16 C34 2 46 6 42 14 C39 21 30 19 24 16 Z"/><circle class="pk-bow__knot" cx="24" cy="16" r="3.2"/></svg>
      </span>
      <p class="pk-banner__text">${t('bannerText', '<strong data-banner-title></strong> is in your bag. Add the product to go inside it.')}</p>
      <a class="pk-banner__cta" href="${data.shopUrl || '/collections/all'}"></a>
      <button type="button" class="pk-banner__close">
        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4 L12 12 M12 4 L4 12"/></svg>
      </button>`;
    banner.querySelector('.pk-banner__cta').textContent = t('chooseProduct', 'Choose a product');
    banner.querySelector('.pk-banner__close').setAttribute('aria-label', t('hideMessage', 'Hide this message'));
    banner.querySelector('.pk-banner__close').addEventListener('click', () => {
      try {
        sessionStorage.setItem(BANNER_DISMISS_KEY, '1');
      } catch (error) {}
      hideBanner();
    });
    document.body.appendChild(banner);
  }

  function showBanner(state, force) {
    if (!banner) buildBanner();
    if (force) {
      try {
        sessionStorage.removeItem(BANNER_DISMISS_KEY);
      } catch (error) {}
    }
    banner.querySelector('[data-banner-title]').textContent = state.title;
    // The CTA is pointless on a page where the picker already offers this product.
    banner.classList.toggle('pk-banner--no-cta', Boolean(document.querySelector('pythal-packaging-picker')));
    banner.hidden = false;
    requestAnimationFrame(() => banner.classList.add('is-visible'));
  }

  function hideBanner() {
    if (!banner) return;
    banner.classList.remove('is-visible');
    banner.hidden = true;
  }

  function updateBanner(state) {
    const onCartPage = /\/cart\/?$/.test(window.location.pathname);
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(BANNER_DISMISS_KEY) === '1';
    } catch (error) {}

    if (state.lock === 'alone' && !onCartPage && !dismissed) showBanner(state);
    else hideBanner();
  }

  /* ---------- Product page picker ---------- */

  class PythalPackagingPicker extends HTMLElement {
    connectedCallback() {
      this.noteElement = this.querySelector('.pk-picker__note');
      this.chosenByShopper = false;
      this.addEventListener('change', () => {
        this.chosenByShopper = true;
      });
      // With no free packaging to fall back on, clicking the chosen card again clears the choice.
      this.lastChecked = this.querySelector('.pk-card__input:checked');
      this.addEventListener('click', (event) => {
        const input = event.target.closest('.pk-card__input');
        if (!input) return;
        if (this.hasAttribute('data-optional') && input === this.lastChecked) {
          input.checked = false;
          this.lastChecked = null;
          this.chosenByShopper = true;
        } else {
          this.lastChecked = input;
        }
      });
      this.update(summarize());
    }

    // null means no packaging chosen: nothing to add.
    selectedVariantId() {
      const checked = this.querySelector('.pk-card__input:checked');
      return checked && checked.value ? Number(checked.value) : null;
    }

    update(state) {
      const bagLine = state.lines[0];
      const bagVariant = bagLine ? Number(bagLine.variant_id) : null;

      this.querySelectorAll('.pk-card__input').forEach((input) => {
        const inBag = bagVariant !== null && Number(input.value) === bagVariant;
        const badge = input.closest('.pk-card').querySelector('.pk-card__inbag');
        if (badge) badge.hidden = !inBag;
        // Show what the bag already has, until the shopper picks something themselves.
        if (inBag && !this.chosenByShopper) {
          input.checked = true;
          this.lastChecked = input;
        }
      });

      this.classList.toggle('pk-picker--free-unlocked', state.freeUnlocked);

      if (this.noteElement) {
        this.noteElement.hidden = !bagLine;
        const title = this.noteElement.querySelector('[data-inbag-title]');
        if (title) title.textContent = state.title;
      }
    }
  }

  if (!customElements.get('pythal-packaging-picker')) {
    customElements.define('pythal-packaging-picker', PythalPackagingPicker);
  }

  /* ---------- Apply state everywhere ---------- */

  function apply() {
    const state = summarize();
    document.documentElement.classList.toggle('pk-locked', Boolean(state.lock));
    applyCheckoutButtons(state);
    document.querySelectorAll('pythal-packaging-picker').forEach((picker) => picker.update(state));
    updateBanner(state);
  }

  // The drawer swaps its markup on every render; re-lock any fresh checkout button.
  let observerFrame;
  new MutationObserver(() => {
    cancelAnimationFrame(observerFrame);
    observerFrame = requestAnimationFrame(() => applyCheckoutButtons(summarize()));
  }).observe(document.body, { childList: true, subtree: true });

  /* ---------- Hooks used by assets/product-form.js ---------- */

  window.PythalPackaging = {
    async beforeAdd(productForm, formData) {
      pendingPackagingKey = null;
      await refresh().catch(() => {});
      const state = summarize();

      if (productForm.dataset.pythalPackaging === 'true') {
        formData.set('quantity', '1');
        if (!state.lines.length) return null;

        if (state.lines.some((line) => Number(line.variant_id) === Number(formData.get('id')))) {
          return { error: t('alreadyInBag', '{{ title }} is already in your bag.', { title: state.title }) };
        }

        // Swap the bag's packaging for this one, keeping the product it was chosen for.
        const packedWith = (state.lines[0].properties || {})[PACKED_WITH];
        const updates = {};
        state.lines.forEach((line) => {
          updates[line.key] = 0;
        });
        try {
          await postJSON(`${cartUrl}/update.js`, { updates });
        } catch (error) {
          return { error: t('changeFailed', "The packaging couldn't be changed: {{ error }}", { error: error.message }) };
        }
        if (packedWith) formData.set(`properties[${PACKED_WITH}]`, packedWith);
        return null;
      }

      // Matched by product, so the sticky bar's form uses the picker too and quick-add of another product does not.
      const productId = formData.get('product-id');
      const picker = productId && document.querySelector(`pythal-packaging-picker[data-product-id="${productId}"]`);
      if (!picker) return null;
      const productTitle = picker.dataset.productTitle;
      const variantId = picker.selectedVariantId();
      const bagLine = state.lines[0];

      // Nothing chosen on an optional picker leaves the bag's packaging alone, unless the shopper cleared it.
      if (!variantId && picker.hasAttribute('data-optional') && !picker.chosenByShopper) return null;

      // Same packaging as the bag already has: just note which product goes inside it, once.
      if (bagLine && variantId === Number(bagLine.variant_id)) {
        const properties = bagLine.properties || {};
        if (!properties[PACKED_WITH]) {
          await postJSON(`${cartUrl}/change.js`, {
            id: bagLine.key,
            quantity: bagLine.quantity,
            properties: { ...properties, [PACKED_WITH]: productTitle }
          }).catch(() => {});
        }
        return null;
      }

      // A different choice replaces what is in the bag, since an order has one packaging.
      if (state.lines.length) {
        const updates = {};
        state.lines.forEach((line) => {
          updates[line.key] = 0;
        });
        try {
          await postJSON(`${cartUrl}/update.js`, { updates });
        } catch (error) {
          return { error: t('changeFailed', "The packaging couldn't be changed: {{ error }}", { error: error.message }) };
        }
      }

      if (!variantId) return null;

      try {
        const added = await postJSON(`${cartUrl}/add.js`, {
          items: [{ id: variantId, quantity: 1, properties: { [PACKED_WITH]: productTitle } }]
        });
        const line = added.items && added.items[0];
        pendingPackagingKey = line ? line.key : null;
        pendingSince = Date.now();
        return null;
      } catch (error) {
        return { error: t('addFailed', "The packaging couldn't be added: {{ error }}", { error: error.message }) };
      }
    },

    // The product failed to add, so the packaging added for it comes out again.
    afterAddFailed() {
      const key = pendingPackagingKey;
      pendingPackagingKey = null;
      if (!key || Date.now() - pendingSince > 30000) return;
      postJSON(`${cartUrl}/change.js`, { id: key, quantity: 0 })
        .then(() => refresh())
        .catch(() => {});
    },

    afterAddSucceeded() {
      pendingPackagingKey = null;
    },

    refresh
  };

  apply();
})();
